/* ==========================================================================
   GWP Progress Bar — "Spend $100, Get a FREE Aussie Birds 3-Pack"
   --------------------------------------------------------------------------
   No libraries. Does NOT re-implement any cart or gift logic — the theme's
   existing SSP Cart Drawer (sections/custom-ssp-cart-drawer.liquid) already
   auto-adds/removes the Aussie Birds 3-Pack at the configured threshold.
   This bar only listens and mirrors the same numbers.

   HOW LIVE UPDATES WORK (no polling):
   1. Primary: the SSP Cart Drawer dispatches `ms:perks-update` (rich payload:
      {total, maxThreshold, percent, unlocked, next}, dollars) and a generic
      `cart:updated` ({ detail: { cart, items } }, cart.total_price in cents)
      on document after every add / qty change / remove / drawer refresh.
   2. Backup: a PerformanceObserver watches for requests to /cart/add,
      /cart/change, /cart/update, /cart/clear made by anything else. If
      neither event above follows shortly, we fetch /cart.js once (debounced,
      previous request aborted).
   3. Back/forward cache: on `pageshow` from bfcache we fetch /cart.js once.
   4. Instant first paint: one /cart.js read on load, ahead of any event.

   THRESHOLD: read live from the cart drawer's own "Free Gift" (ms_tier)
   block via #ms-perks-data whenever that block exists, so this bar and the
   drawer can never drift apart. The Theme Editor "Spend threshold" setting
   is only a fallback for when that block can't be found.

   MONEY: cart.total_price is in CENTS. Dollar settings are converted once.
   ========================================================================== */
(function () {
  'use strict';

  if (window.SSPGwpProgressBar && window.SSPGwpProgressBar.__loaded) {
    window.SSPGwpProgressBar.initAll();
    return;
  }

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var CELEBRATED_KEY = 'gwpPromoCelebrated';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  var instances = [];
  var lastCartAt = 0;
  var fetchTimer = null;
  var fetchCtrl = null;
  var fetchSeq = 0;

  /* ---------------- Money helpers (dollars in, string out) ---------------- */
  function money(amount) {
    amount = Math.max(0, Math.ceil(Number(amount) || 0));
    return '$' + amount.toLocaleString();
  }
  function moneyExact(amount) {
    amount = Math.max(0, Number(amount) || 0);
    return '$' + amount.toFixed(2).replace(/\.00$/, '');
  }

  /* ---------------- Live threshold sync ----------------
     Read the cart drawer's own "Free Gift" (ms_tier) milestone block when it
     exists, so the two components can never drift. */
  function getLiveThreshold(fallback) {
    try {
      var perksEl = document.getElementById('ms-perks-data');
      if (!perksEl) return fallback;
      var data = JSON.parse(perksEl.textContent);
      var giftTier = (data.tiers || []).filter(function (t) { return t.kind === 'ms_tier'; })[0];
      if (giftTier && typeof giftTier.threshold === 'number' && giftTier.threshold > 0) {
        return giftTier.threshold;
      }
    } catch (e) {}
    return fallback;
  }

  function isGiftLine(item, gwpVariantId) {
    var p = item.properties || {};
    return p._ms_gift === '1' || p._gwp_promo === '1' ||
      (gwpVariantId && Number(item.variant_id) === Number(gwpVariantId));
  }
  // Qualifying dollars = cart.total_price (already excludes shipping/tax),
  // minus any free-gift line so the gift can never help unlock itself.
  function qualifyingDollars(cart, gwpVariantId) {
    var totalCents = Number(cart.total_price) || 0;
    (cart.items || []).forEach(function (item) {
      if (isGiftLine(item, gwpVariantId) || item.gift_card) {
        totalCents -= Number(item.final_line_price != null ? item.final_line_price : item.line_price) || 0;
      }
    });
    return Math.max(0, totalCents) / 100;
  }

  /* ---------------- Instance ---------------- */
  function Bar(root) {
    this.root = root;
    try {
      this.cfg = JSON.parse(root.querySelector('[data-gwp-promo-config]').textContent);
    } catch (e) {
      this.cfg = null;
      return;
    }
    this.toggle = root.querySelector('[data-gwp-promo-toggle]');
    this.panel = root.querySelector('[data-gwp-promo-panel]');
    this.msgD = root.querySelector('[data-gwp-promo-msg="desktop"]');
    this.msgM = root.querySelector('[data-gwp-promo-msg="mobile"]');
    this.fills = root.querySelectorAll('[data-gwp-promo-fill]');
    this.progressbar = root.querySelector('[data-gwp-promo-progressbar]');
    this.totalEl = root.querySelector('[data-gwp-promo-total]');
    this.amountEl = root.querySelector('[data-gwp-promo-amount]');
    this.live = root.querySelector('[data-gwp-promo-live]');
    this.confetti = root.querySelector('[data-gwp-promo-confetti]');
    this.state = root.getAttribute('data-state') || 'zero';
    this.total = Number(this.cfg.initialTotal) || 0;
    this.bind();
  }

  Bar.prototype.threshold = function () {
    return getLiveThreshold(Number(this.cfg.threshold) || 100);
  };

  Bar.prototype.stateFor = function (total, threshold) {
    if (total >= threshold) return 'unlocked';
    var remaining = threshold - total;
    if (total <= 0) return 'zero';
    if (remaining <= 15) return 'close';
    if (remaining <= 30) return 'almost';
    return 'progress';
  };

  Bar.prototype.fill = function (tpl, vars) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : m;
    });
  };

  Bar.prototype.render = function (total) {
    var c = this.cfg;
    total = Math.max(0, Number(total) || 0);
    var threshold = this.threshold();
    var prevState = this.state;
    var state = this.stateFor(total, threshold);
    var remaining = Math.max(0, threshold - total);
    var pct = threshold > 0 ? Math.min(100, (total / threshold) * 100) : 100;
    var msgs = c.messages[state] || {};

    this.total = total;
    this.state = state;
    this.root.setAttribute('data-state', state);

    var vars = { amount: money(remaining), gift: c.giftName, value: c.giftValue, threshold: money(threshold) };
    var dText = this.fill(msgs.desktop, vars);
    var mText = this.fill(msgs.mobile || msgs.desktop, vars);
    if (this.msgD && this.msgD.textContent !== dText) this.msgD.textContent = dText;
    if (this.msgM && this.msgM.textContent !== mText) this.msgM.textContent = mText;

    for (var i = 0; i < this.fills.length; i++) this.fills[i].style.width = pct.toFixed(2) + '%';
    if (this.progressbar) this.progressbar.setAttribute('aria-valuenow', String(Math.round(pct)));
    if (this.totalEl) this.totalEl.textContent = moneyExact(total);

    if (this.amountEl) {
      if (state === 'unlocked') {
        this.amountEl.innerHTML = '<span class="gwp-promo__tick" aria-hidden="true">✓</span> ';
        this.amountEl.appendChild(document.createTextNode(this.fill(c.panelAmount.unlocked, vars)));
      } else {
        this.amountEl.textContent = this.fill(c.panelAmount.locked, vars);
      }
    }

    if (this.live && (prevState !== state)) this.live.textContent = mText;
    if (prevState !== 'unlocked' && state === 'unlocked') this.celebrate();
  };

  Bar.prototype.celebrate = function () {
    if (!this.cfg.celebrate) return;
    if (reduceMotion && reduceMotion.matches) return;
    try {
      if (sessionStorage.getItem(CELEBRATED_KEY)) return;
      sessionStorage.setItem(CELEBRATED_KEY, '1');
    } catch (e) {}

    var root = this.root;
    var box = this.confetti;
    root.classList.add('is-celebrating');
    if (box) {
      var colours = ['#f5c346', '#ffffff', '#003b73', '#0074b7'];
      var frag = document.createDocumentFragment();
      for (var i = 0; i < 24; i++) {
        var p = document.createElement('i');
        p.style.left = (Math.random() * 100).toFixed(1) + '%';
        p.style.background = colours[i % colours.length];
        p.style.animationDelay = (Math.random() * 0.35).toFixed(2) + 's';
        frag.appendChild(p);
      }
      box.appendChild(frag);
    }
    setTimeout(function () {
      root.classList.remove('is-celebrating');
      if (box) box.innerHTML = '';
    }, 1800);
  };

  Bar.prototype.setOpen = function (open) {
    if (!this.toggle || !this.panel) return;
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.root.classList.toggle('is-open', open);
  };

  Bar.prototype.bind = function () {
    var self = this;
    if (this.toggle) {
      this.toggle.addEventListener('click', function () {
        self.setOpen(self.toggle.getAttribute('aria-expanded') !== 'true');
      });
      this.root.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && self.root.classList.contains('is-open')) {
          self.setOpen(false);
          self.toggle.focus();
        }
      });
    }
  };

  /* ---------------- Cart plumbing ---------------- */
  function applyCart(cart) {
    if (!cart || typeof cart.total_price === 'undefined') return;
    lastCartAt = Date.now();
    instances.forEach(function (b) {
      b.render(qualifyingDollars(cart, b.cfg.gwpVariantId));
    });
  }

  function applyTotalDollars(total) {
    lastCartAt = Date.now();
    instances.forEach(function (b) { b.render(total); });
  }

  function fetchCart() {
    if (fetchCtrl) { try { fetchCtrl.abort(); } catch (e) {} }
    fetchCtrl = window.AbortController ? new AbortController() : null;
    var seq = ++fetchSeq;
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: fetchCtrl ? fetchCtrl.signal : undefined
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (seq === fetchSeq) applyCart(cart); })
      .catch(function () {});
  }

  function scheduleFetch(delay) {
    var requestedAt = Date.now();
    clearTimeout(fetchTimer);
    fetchTimer = setTimeout(function () {
      if (lastCartAt > requestedAt) return; // a newer signal already arrived
      fetchCart();
    }, delay || 350);
  }

  function listen() {
    // 1) SSP Cart Drawer's dedicated broadcast (dollars, already-qualifying)
    document.addEventListener('ms:perks-update', function (e) {
      if (e.detail && typeof e.detail.total === 'number') {
        clearTimeout(fetchTimer);
        applyTotalDollars(e.detail.total);
      }
    });

    // 2) SSP Cart Drawer's generic broadcast (full cart object)
    document.addEventListener('cart:updated', function (e) {
      var cart = e && e.detail && e.detail.cart;
      if (cart && typeof cart.total_price !== 'undefined') {
        clearTimeout(fetchTimer);
        applyCart(cart);
      } else {
        scheduleFetch(150);
      }
    });

    // 3) Fallback: observe cart mutation requests (no polling, no monkey-patching)
    if ('PerformanceObserver' in window) {
      try {
        var po = new PerformanceObserver(function (list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            if (CART_MUTATION_RE.test(entries[i].name)) { scheduleFetch(600); return; }
          }
        });
        po.observe({ type: 'resource', buffered: false });
      } catch (e) {}
    }

    // 4) Back/forward cache restores an old DOM: refresh once.
    window.addEventListener('pageshow', function (e) { if (e.persisted) fetchCart(); });

    // 5) Theme Editor: re-init when the section is re-rendered.
    document.addEventListener('shopify:section:load', function () { initAll(); });
    document.addEventListener('shopify:section:unload', function () {
      setTimeout(function () {
        instances = instances.filter(function (b) { return document.body.contains(b.root); });
      }, 0);
    });
  }

  /* ---------------- Init ---------------- */
  function initAll() {
    var roots = document.querySelectorAll('[data-gwp-promo]');
    instances = instances.filter(function (b) { return document.body.contains(b.root); });
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__gwpPromo) continue;
      var b = new Bar(roots[i]);
      if (!b.cfg) continue;
      roots[i].__gwpPromo = b;
      instances.push(b);
    }
  }

  window.SSPGwpProgressBar = {
    __loaded: true,
    initAll: initAll,
    refresh: fetchCart,
    update: applyCart
  };

  function start() {
    initAll();
    listen();
    fetchCart(); // instant first paint, ahead of any cart drawer event
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
